/* eslint-disable complexity */
/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useRef, useState } from 'react';
import { CoreGlobalFooter } from '@care/navigation';
import { Container, Grid } from '@mui/material';

import { costGuide_costGuide_CostGuideSuccess_costGuide as CostGuideType } from '@/__generated__/costGuide';
import { costGuideComparisons_costGuideComparisons_CostGuideComparisonsSuccess_costGuideComparisons as costGuideComparisonsType } from '@/__generated__/CostGuideComparisons';
import { costGuideServiceSpecialties_costGuideServiceSpecialties_CostGuideServiceSpecialtiesSuccess_serviceSpecialties_specialties as costGuideServiceSpecialtiesType } from '@/__generated__/costGuideServiceSpecialties';
import { getSEOSegmentationLinkSections as CostGuideLinkSections } from '@/__generated__/getSEOSegmentationLinkSections';
import NavBar from '@/components/SeoGlobalNavbar';
import HowItWorks from '@/components/HowItWorks';

import generateEnrollmentUrl from '@/utils/generateEnrollmentUrl';
import createRelativeImageURL from '@/utils/createRelativeImageURL';
import {
  AmplitudeEvent,
  setDeviceDataFromHeaders,
  setPagePropertiesFromData,
  trackAmplitudeEvent,
} from '@/lib/analyticsHelper';
import {
  COST_GUIDE_HERO_BG_IMG_URL,
  mapServiceTypeToCzenServiceId,
  mapServiceIdToMemberEnumToString,
  ScreenTemplate,
  LaunchDarklyEvaluationReasons,
  UI_FEATURE_FLAGS,
  HTL_SLOTS,
} from '@/constants';
import PageMeta from '@/components/PageMeta';
import dateMonthYearFormat from '@/utils/dateMonthYearFormat';
import { useLDClientFlags } from '@/components/hooks/useLDClientFlags';
import { GAMAds } from '@/components/GAMAds';
import CostGuideLinkList from './CostGuideLinkList';
import SlimSearch from './CostGuideSlimSearch';
import ComparisonTable from './ComparisonTable';
import Hero from './Hero';
import CostGuide404 from './CostGuide404';
import { CostGuidePriceSpecificationSchema } from './CostGuidePriceSpecificationSchema';

export type CostGuidePageProps = {
  pageData: {
    costGuide: CostGuideType;
    costGuideComparisons: costGuideComparisonsType;
    costGuideLinkSections: CostGuideLinkSections['getSEOSegmentationLinkSections'];
    costGuideServiceSpecialties?: costGuideServiceSpecialtiesType[];
  };
  seoSegmentId: string;
  deviceChar: {
    device_os: string;
    is_mobile: any;
    mobile_browser_version: any;
    mobile_browser: string;
  };
  errors?: any;
};

const CostGuidePage = (props: CostGuidePageProps) => {
  const { deviceChar, seoSegmentId, errors } = props;
  const { SEO_3487_GAM_ADS_SEO_MFE: GAMFlag } = useLDClientFlags();
  const showGAMAds = GAMFlag && GAMFlag.variationIndex === 1;
  const gamRef = useRef<HTMLDivElement | null>(null);
  const [gamIsVisible, setGamIsVisible] = useState<boolean>(false);

  if (errors) {
    return <CostGuide404 seoSegmentId={seoSegmentId} deviceChar={deviceChar} />;
  }

  const {
    pageData: {
      costGuide: {
        category,
        location,
        meta,
        primaryHeadline,
        rates: { averageRate, lastUpdatedDate },
        searchDefaults,
        searchDefaults: { service, location: searchDefaultLocation },
        service: { czenServiceId, serviceIdForMember: serviceIdForMemberEnum },
      },
      costGuideComparisons: {
        geographicRateComparison,
        mostAffordableRateComparison,
        mostExpensiveRateComparison,
      },
      costGuideLinkSections,
      costGuideServiceSpecialties,
    },
  } = props;

  const monthYearDate = lastUpdatedDate
    ? dateMonthYearFormat({
        dateTime: lastUpdatedDate,
        useLongMonth: true,
      })
    : null;
  const serviceNameLowercase = category?.categoryName?.categoryNameSingularLowerCase ?? 'service';
  const serviceNamePluralLowercase =
    category?.categoryName?.categoryNamePluralLowerCase ?? 'services';
  const serviceIdForMember = mapServiceIdToMemberEnumToString[serviceIdForMemberEnum];
  const serviceId = mapServiceTypeToCzenServiceId[czenServiceId];
  const enrollmentUrl = generateEnrollmentUrl({
    serviceIdForMember,
    serviceId: serviceId ?? '',
  });
  const segmentationUrl = searchDefaultLocation
    ? `/${service.slug}/${searchDefaultLocation.slug}`
    : `/${service.slug}`;
  const fallBackTitle = `How much does a ${serviceNameLowercase} cost in ${location?.city}, ${location?.state.stateCode}?`;
  const fallBackDescription = `Find how much to pay for a ${serviceNameLowercase} in ${location?.city}, ${location?.state.stateCode} with latest data from ${monthYearDate}. ${location?.city}, ${location?.state.stateCode} ${serviceNameLowercase} pay rates are based on latest rates reported by providers listed on Care.com.`;
  const fallBackCanonicalUrl = `https://www.care.com/cost${segmentationUrl}`;

  useEffect(() => {
    const pageProperties = {
      screen_name: 'Cost Guide Page',
      screen_template: ScreenTemplate.COST_GUIDE,
      seo_page_id: 1533,
      service_id_for_member: serviceIdForMember,
    };
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setPagePropertiesFromData(pageProperties);
      setDeviceDataFromHeaders(deviceChar);
    }
  }, [deviceChar, serviceIdForMember]);

  useEffect(() => {
    const amplitudeDataForPageViewed = {
      seo_page_title: primaryHeadline,
      seo_page_type: 'Caregiver Page',
      page_template: 'Local',
    };
    trackAmplitudeEvent(AmplitudeEvent.seoPageViewed, amplitudeDataForPageViewed);
  }, [primaryHeadline]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setGamIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.5,
      }
    );

    const currentGamRef = gamRef.current;

    if (currentGamRef) {
      observer.observe(currentGamRef);
    }

    return () => {
      if (currentGamRef) {
        observer.unobserve(currentGamRef);
      }
    };
  }, [gamRef]);

  useEffect(() => {
    if (
      gamIsVisible &&
      GAMFlag &&
      GAMFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3487_GAM_ADS_SEO_MFE.id,
        test_variant: GAMFlag.variationIndex,
        service_id_for_member: serviceIdForMember,
        page_template: 'Local',
      });
    }
  }, [GAMFlag, gamIsVisible, serviceIdForMember]);

  return (
    <>
      <PageMeta
        title={meta?.title ?? fallBackTitle}
        metaDescriptions={meta?.description ?? fallBackDescription}
        canonicalUrl={meta?.canonicalURL ?? fallBackCanonicalUrl}
        isCostGuidePage
      />
      {averageRate?.averageHourlyRate && (
        <CostGuidePriceSpecificationSchema averageHourlyRate={averageRate.averageHourlyRate} />
      )}
      <NavBar enrollmentURL={enrollmentUrl} />
      <SlimSearch
        searchDefaults={searchDefaults}
        costGuideServiceSpecialties={costGuideServiceSpecialties}
      />
      <Grid
        container
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundImage: `url(${createRelativeImageURL(COST_GUIDE_HERO_BG_IMG_URL)}?im=)`,
        }}>
        <Hero
          city={location?.city}
          enrollmentUrl={enrollmentUrl}
          primaryHeadline={primaryHeadline}
          rateLastUpdatedDate={monthYearDate}
          serviceNameLowercase={serviceNameLowercase}
          state={location?.state?.stateCode ?? null}
          averageRates={averageRate}
          serviceNamePluralLowercase={serviceNamePluralLowercase}
        />
      </Grid>
      {showGAMAds && (
        <Grid ref={gamRef} m="0 auto">
          <GAMAds slotName={HTL_SLOTS.SEO_COST_INLINE_1} />
        </Grid>
      )}
      {geographicRateComparison && (
        <Container maxWidth="md" disableGutters>
          <ComparisonTable
            currentCity={location?.city ?? null}
            currentStateCode={location?.state.stateCode ?? null}
            serviceNameLowercase={serviceNameLowercase}
            serviceNamePluralLowercase={serviceNamePluralLowercase}
            tableData={geographicRateComparison}
            isGeographicComparison
            key="geographicRate"
          />
        </Container>
      )}
      {mostAffordableRateComparison && (
        <Container maxWidth="md" disableGutters>
          <ComparisonTable
            currentCity={location?.city ?? null}
            currentStateCode={location?.state.stateCode ?? null}
            serviceNameLowercase={serviceNameLowercase}
            serviceNamePluralLowercase={serviceNamePluralLowercase}
            tableData={mostAffordableRateComparison}
            isMostAffordableCities
            key="mostAffordableRate"
          />
        </Container>
      )}
      {showGAMAds && <GAMAds slotName={HTL_SLOTS.SEO_COST_INLINE_2} />}
      {mostExpensiveRateComparison && (
        <Container maxWidth="md" disableGutters>
          <ComparisonTable
            currentCity={location?.city ?? null}
            currentStateCode={location?.state.stateCode ?? null}
            serviceNameLowercase={serviceNameLowercase}
            serviceNamePluralLowercase={serviceNamePluralLowercase}
            tableData={mostExpensiveRateComparison}
            key="mostExpensiveRate"
          />
        </Container>
      )}

      <Grid item xs={12} sx={{ backgroundColor: 'care.grey.100' }}>
        <Container maxWidth="md" disableGutters>
          <HowItWorks
            serviceIdForMember={serviceIdForMember}
            segmentNamePluralLowerCase={serviceNamePluralLowercase}
            segmentNameSingularLowerCase={serviceNameLowercase}
            segmentationUrl={segmentationUrl}
            isCostGuidePage
          />
        </Container>
      </Grid>

      {costGuideLinkSections && (
        <CostGuideLinkList
          costGuideLinkList={costGuideLinkSections}
          city={location?.city ?? null}
          stateCode={location?.state.stateCode ?? null}
        />
      )}
      <CoreGlobalFooter />
    </>
  );
};

export default CostGuidePage;
